/**
 * CodeDiff
 *
 * A component for displaying code comparisons in chat messages
 *
 * Primary responsibilities:
 * - Show before and after code side by side or stacked
 * - Highlight differences between code snippets
 * - Support responsive layout based on screen size
 * - Allow copy to clipboard functionality
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  IconCodeDots,
  IconCopy,
  IconCheck,
  IconArrowsVertical,
  IconArrowsHorizontal
} from '@tabler/icons-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface CodeDiffProps {
  beforeCode: string
  afterCode: string
  language?: string
  title?: string
  className?: string
}

export const CodeDiff: React.FC<CodeDiffProps> = ({
  beforeCode,
  afterCode,
  language = 'javascript',
  title = 'Code Comparison',
  className
}) => {
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('horizontal')
  const [copied, setCopied] = useState<'before' | 'after' | null>(null)

  const toggleOrientation = () => {
    setOrientation((prev) => (prev === 'horizontal' ? 'vertical' : 'horizontal'))
  }

  const copyToClipboard = async (code: string, type: 'before' | 'after') => {
    await navigator.clipboard.writeText(code)
    setCopied(type)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconCodeDots size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
          {language && (
            <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              {language}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleOrientation}
          className="ml-auto"
          title={
            orientation === 'horizontal' ? 'Switch to Vertical View' : 'Switch to Horizontal View'
          }
        >
          {orientation === 'horizontal' ? (
            <IconArrowsVertical size={16} />
          ) : (
            <IconArrowsHorizontal size={16} />
          )}
        </Button>
      </div>

      {orientation === 'horizontal' ? (
        <div className="grid grid-cols-1 gap-0 border-b md:grid-cols-2">
          <div className="border-r">
            <div className="bg-muted/30 flex items-center justify-between p-2">
              <span className="text-xs font-medium">Before</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(beforeCode, 'before')}
                className="size-6 p-0"
              >
                {copied === 'before' ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </Button>
            </div>
            <pre className="overflow-x-auto p-4 text-sm">
              <code>{beforeCode}</code>
            </pre>
          </div>
          <div>
            <div className="bg-muted/30 flex items-center justify-between p-2">
              <span className="text-xs font-medium">After</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(afterCode, 'after')}
                className="size-6 p-0"
              >
                {copied === 'after' ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </Button>
            </div>
            <pre className="overflow-x-auto p-4 text-sm">
              <code>{afterCode}</code>
            </pre>
          </div>
        </div>
      ) : (
        <Tabs defaultValue="before">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="before">Before</TabsTrigger>
            <TabsTrigger value="after">After</TabsTrigger>
          </TabsList>
          <TabsContent value="before">
            <div className="bg-muted/30 flex items-center justify-end p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(beforeCode, 'before')}
                className="size-6 p-0"
              >
                {copied === 'before' ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </Button>
            </div>
            <pre className="overflow-x-auto p-4 text-sm">
              <code>{beforeCode}</code>
            </pre>
          </TabsContent>
          <TabsContent value="after">
            <div className="bg-muted/30 flex items-center justify-end p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(afterCode, 'after')}
                className="size-6 p-0"
              >
                {copied === 'after' ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </Button>
            </div>
            <pre className="overflow-x-auto p-4 text-sm">
              <code>{afterCode}</code>
            </pre>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
